import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import BarChart from "@cloudscape-design/components/bar-chart";
import Box from "@cloudscape-design/components/box";
import Button from "@mui/material/Button";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import downloadSLACertificate from "./SpecificEndpointReport";
import { Paper } from "@mui/material";
import UnauthorizedPage from "../UnauthorizedPage";
import LoadingEndpointSpecific from "./LoadingEndpointSpecific";
const apiUrl = process.env.REACT_APP_API_URL;

const theme = createTheme({
  palette: {
    primary: {
      main: "#003366",
    },
  },
});

export default function SpecificEndpoint() {
  const { endpointName } = useParams();
  const [data, setData] = useState(null);
  const [logData, setLogData] = useState("");
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState("7days");
  let accessToken = useSelector((state) => state.accessToken.value);

  const query = `query MyQuery($endpointName: String!, $duration: String!) {
    getEndpointStatusDetails(endpointName: $endpointName, duration: $duration) {
      topResults {
        endpointname
        logdata
        responsetime
        status
        timestamp
      }
      graphresultsArray {
        averagePerDayData {
          avgAvailability
          timeInterval
        }
        endpointName
      }
      totalDownRequests
      totalRequests
    }
  }`;

  const variables = { endpointName, duration };

  const getData = async () => {
    setData(null);
    setLogData("");
    try {
      const response = await axios.post(
        apiUrl,
        { query, variables },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (
        response.data &&
        response.data.data &&
        response.data.data.getEndpointStatusDetails
      ) {
        const results = response.data.data.getEndpointStatusDetails;
        setData(results);
        setLogData(results.topResults[0].logdata);
      }

      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setError("Unauthorized");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getData();
  }, [endpointName, duration]);

  const handleRowClick = (log) => {
    setLogData(log);
  };

  const chartData =
    data?.graphresultsArray?.[0]?.averagePerDayData.map((d) => ({
      x: new Date(d.timeInterval),
      y: d.avgAvailability,
    })) || [];

  const totalRequests = data?.totalRequests || 0;
  const totalDownRequests = data?.totalDownRequests || 0;
  const availabilityPercentage = totalRequests
    ? ((totalRequests - totalDownRequests) / totalRequests) * 100
    : 0;

if(error == 'Unauthorized'){
  return <UnauthorizedPage/>
}

  return (
    <ThemeProvider theme={theme}>
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-[#003366]">Endpoints</h1>

            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Paper
                elevation={0}
                sx={{
                  display: "inline-flex",
                  p: 0.5,
                  borderRadius: 2,
                  bgcolor: "#FFFFFF",
                }}
              >
                {["7days", "1month", "3months"].map((option) => (
                  <Button
                    key={option}
                    onClick={() => setDuration(option)}
                    variant={duration === option ? "contained" : "text"}
                    size="small"
                    sx={{
                      mx: 0.5,
                      textTransform: "none",
                      minWidth: "auto",
                      bgcolor:
                        duration === option ? "primary.main" : "transparent",
                      "&:hover": {
                        bgcolor:
                          duration === option
                            ? "primary.dark"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {option === "7days"
                      ? "7 Days"
                      : option === "1month"
                        ? "1 Month"
                        : "3 Months"}
                  </Button>
                ))}
              </Paper>
            </Box>

            <div className="flex items-center space-x-2 text-[#003366]">
              <Link to="/endpoint" className="hover:text-blue-700">
                Endpoint
              </Link>
              <span>&gt;</span>
              <span className="font-medium">{endpointName}</span>
            </div>
          </div>
          {!data ? (
            <LoadingEndpointSpecific/>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                  <div className="text-[#003366] h-full flex flex-col justify-center space-y-6">
                    <div className="flex justify-center">
                      <svg
                        className="w-8 h-8"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-center">
                      Average Availability
                    </h3>
                    <p className="text-center text-sm text-gray-600">
                      Average availability over the given time period
                    </p>
                    <div className="text-4xl font-bold text-center text-green-600">
                      {availabilityPercentage.toFixed(2)}%
                    </div>
                    <div className="flex justify-center">
                      <Button
                        onClick={() =>
                          downloadSLACertificate(
                            availabilityPercentage,
                            endpointName,
                            totalDownRequests,
                            chartData,
                            data.topResults
                          )
                        }
                        variant="contained"
                        sx={{
                          bgcolor: "primary.main",
                          "&:hover": {
                            bgcolor: "primary.dark",
                          },
                        }}
                      >
                        Download Report
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                  <BarChart
                    series={[
                      {
                        title: "Availability %",
                        type: "bar",
                        data: chartData,
                        valueFormatter: (value) => `${value.toFixed(2)}%`,
                        color: "#003366",
                      },
                    ]}
                    xDomain={chartData.map((d) => d.x)}
                    yDomain={[0, 100]}
                    i18nStrings={{
                      xTickFormatter: (date) =>
                        date
                          .toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                            hour12: false,
                          })
                          .split(",")
                          .join("\n"),
                      yTickFormatter: (value) => `${value.toFixed(0)}%`,
                    }}
                    ariaLabel="Endpoint availability over time"
                    height={200}
                    xTitle="Time (UTC)"
                    yTitle="Availability (%)"
                    empty={
                      <Box textAlign="center" color="inherit">
                        <b>No data available</b>
                      </Box>
                    }
                    noMatch={
                      <Box textAlign="center" color="inherit">
                        <b>No matching data</b>
                      </Box>
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-[#003366] mb-4">
                    Status History
                  </h3>
                  <div className="overflow-x-auto max-h-[400px]">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-[#003366]/5">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                            Timestamp
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {data?.topResults?.map((row, i) => (
                          <tr
                            key={i}
                            onClick={() => handleRowClick(row.logdata)}
                            className="hover:bg-[#003366]/5 cursor-pointer"
                          >
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                              {row.timestamp}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  row.status === "Running"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {row.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
                  <h3 className="text-lg font-bold text-[#003366] mb-4">
                    Log Data
                  </h3>
                  <div className="bg-gray-50 rounded p-4 max-h-[400px] overflow-y-auto">
                    <pre className="text-sm text-gray-700 font-mono whitespace-pre-wrap break-words">
                      {logData || "No log data available"}
                    </pre>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
}
